import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Grid,
  Group,
  Modal,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconEye,
  IconFileInvoice,
  IconLock,
  IconPrinter,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { Link, useSearchParams } from "react-router-dom";
import { ReportExportButton } from "../../components/reports/ReportExportButton";
import {
  useBillingConfiguration,
  useMarkManualEntry,
} from "../../hooks/useBilling";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useManualTebraEntryReport } from "../../hooks/useReports";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import {
  type ManualTebraAddress,
  type ManualTebraEntryCase,
  type ManualTebraEntryParams,
  type ManualTebraInsurance,
  type ManualTebraReadiness,
  type ManualTebraStatus,
  reportsService,
} from "../../services/reports.service";
import {
  canConfirmManualTebraEntry,
  getManualTebraServiceLines,
  totalManualTebraCharges,
  validateManualEntryIdentifiers,
} from "../../utils/manualTebraEntry";
import { LAB_TIME_ZONE } from "../../utils/labTime";
import type { DateOnlyString } from "../../utils/dateOnly";
import {
  buildManualTebraDateParams,
  formatManualTebraCalendarDate,
  normalizeManualTebraPickerDate,
} from "./manualTebraDate";

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: "attention", label: "Awaiting action" },
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Awaiting entry" },
  { value: "error", label: "API error" },
  { value: "rejected", label: "API rejected" },
  { value: "reconciliation_required", label: "Reconciliation required" },
  { value: "submitted", label: "Submitted" },
  { value: "billed", label: "Billed" },
];

type ManualTebraStatusSelection = ManualTebraStatus | "attention";

const READINESS_OPTIONS = [
  { value: "all", label: "All readiness" },
  { value: "ready", label: "Ready" },
  { value: "legacy_blocked", label: "Legacy blocked" },
  { value: "blocked", label: "Blocked" },
];

function textOrDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatInstantDate(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: LAB_TIME_ZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsed);
}

function formatMoney(value: number | null): string {
  if (value === null) return "Missing";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
}

function formatAddress(address?: ManualTebraAddress | null): string {
  if (!address) return "—";
  return (
    [
      address.line1,
      address.line2,
      [address.city, address.state].filter(Boolean).join(", "),
      address.zip,
      address.country,
    ]
      .filter(Boolean)
      .join(" · ") || "—"
  );
}

function patientName(packet: ManualTebraEntryCase): string {
  const patient = packet.patient;
  if (!patient) return "Missing patient";
  return [patient.firstName, patient.middleName, patient.lastName]
    .filter(Boolean)
    .join(" ");
}

function statusBadge(packet: ManualTebraEntryCase) {
  const method = packet.billingSubmissionMethod;
  if (
    packet.billingStatus === "submitted" ||
    packet.billingStatus === "billed"
  ) {
    return (
      <Group gap={4} wrap="nowrap">
        <Badge color="green" variant="light">
          {packet.billingStatus === "billed" ? "Billed" : "Entered"}
        </Badge>
        {method && (
          <Badge color={method === "manual" ? "blue" : "grape"} variant="dot">
            {method === "manual" ? "Manual" : "API"}
          </Badge>
        )}
      </Group>
    );
  }

  const color =
    packet.billingStatus === "pending"
      ? "yellow"
      : packet.billingStatus === "reconciliation_required"
      ? "grape"
      : "red";
  return (
    <Badge color={color} variant="light">
      {packet.billingStatus.replace(/_/g, " ")}
    </Badge>
  );
}

function ReadinessBadge({ packet }: { packet: ManualTebraEntryCase }) {
  if (packet.billingFactsSource === "legacy_live") {
    return packet.readiness.ready ? (
      <Badge color="orange" leftSection={<IconAlertTriangle size={11} />}>
        Legacy · Blocked
      </Badge>
    ) : (
      <Group gap={4} wrap="nowrap">
        <Badge color="red">Blocked ({packet.readiness.errors.length})</Badge>
        <Badge color="orange" variant="light">
          Legacy
        </Badge>
      </Group>
    );
  }

  return packet.readiness.ready ? (
    <Badge color="green" leftSection={<IconCheck size={11} />}>
      Ready
    </Badge>
  ) : (
    <Tooltip
      label={packet.readiness.errors.join(" · ") || "Missing required data"}
      multiline
      w={380}
    >
      <Badge color="red" leftSection={<IconAlertTriangle size={11} />}>
        Blocked ({packet.readiness.errors.length})
      </Badge>
    </Tooltip>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      <Text size="sm" style={{ overflowWrap: "anywhere" }}>
        {value || "—"}
      </Text>
    </div>
  );
}

function InsuranceSection({
  title,
  insurance,
}: {
  title: string;
  insurance: ManualTebraInsurance | null;
}) {
  if (!insurance) {
    return (
      <Paper withBorder p="md">
        <Title order={5}>{title}</Title>
        <Text size="sm" c="dimmed" mt="xs">
          No policy recorded.
        </Text>
      </Paper>
    );
  }

  const subscriberName =
    insurance.subscriberName ||
    [
      insurance.subscriberFirstName,
      insurance.subscriberMiddleName,
      insurance.subscriberLastName,
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Title order={5}>{title}</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
          <Field label="Payer" value={textOrDash(insurance.providerName)} />
          <Field label="Plan type" value={textOrDash(insurance.planTypeName)} />
          <Field
            label="Policy / member ID"
            value={textOrDash(insurance.policyNumber)}
          />
          <Field
            label="Group number"
            value={textOrDash(insurance.groupNumber)}
          />
          <Field label="Group name" value={textOrDash(insurance.groupName)} />
          <Field
            label="Patient relationship"
            value={textOrDash(insurance.relationship)}
          />
          <Field
            label="Tebra company ID"
            value={textOrDash(insurance.companyId)}
          />
          <Field label="Tebra plan ID" value={textOrDash(insurance.planId)} />
          <Field label="Payer phone" value={textOrDash(insurance.phone)} />
          <Field
            label="Authorization"
            value={textOrDash(insurance.authorizationNumber)}
          />
          <Field label="Subscriber" value={textOrDash(subscriberName)} />
          <Field
            label="Subscriber DOB"
            value={formatManualTebraCalendarDate(insurance.subscriberDob)}
          />
          <Field
            label="Subscriber sex"
            value={textOrDash(insurance.subscriberSex)}
          />
          <Field
            label="Payer / plan address"
            value={formatAddress(insurance.address)}
          />
          <Field
            label="Subscriber address"
            value={formatAddress(insurance.subscriberAddress)}
          />
        </SimpleGrid>
      </Stack>
    </Paper>
  );
}

function PacketDetails({ packet }: { packet: ManualTebraEntryCase }) {
  const patient = packet.patient;
  const destination = packet.destination;
  const isLegacy = packet.billingFactsSource === "legacy_live";
  const lines = isLegacy ? [] : getManualTebraServiceLines(packet);

  return (
    <Stack gap="md">
      <Alert
        color={isLegacy ? "orange" : packet.readiness.ready ? "green" : "red"}
        icon={
          packet.readiness.ready && !isLegacy ? (
            <IconCheck size={18} />
          ) : (
            <IconAlertTriangle size={18} />
          )
        }
        title={
          isLegacy
            ? "LEGACY · BLOCKED — service lines are not signed-snapshot facts"
            : packet.readiness.ready
            ? "READY for manual Tebra entry"
            : "BLOCKED — correct these items before entering or recording"
        }
      >
        {isLegacy && (
          <Stack gap={3} mb="xs">
            <Text size="sm" fw={600}>
              These UNVERIFIED candidate lines were compiled from the current
              LIS, not recovered from a signed billing snapshot. Do not key them
              into Tebra. A pathologist must create and sign a corrected or
              addendum report first.
            </Text>
          </Stack>
        )}
        {packet.readiness.errors.length > 0 && (
          <Stack gap={2} mb={packet.readiness.warnings.length ? "xs" : 0}>
            {packet.readiness.errors.map((error) => (
              <Text key={error} size="sm">
                • {error}
              </Text>
            ))}
          </Stack>
        )}
        {packet.readiness.warnings.map((warning) => (
          <Text key={warning} size="sm" c="orange.9">
            Warning: {warning}
          </Text>
        ))}
        {packet.readiness.ready &&
          !isLegacy &&
          packet.readiness.warnings.length === 0 && (
            <Text size="sm">All required packet fields are present.</Text>
          )}
      </Alert>

      <Paper withBorder p="md">
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={4}>{packet.caseNumber}</Title>
            <Group gap="xs">
              <ReadinessBadge packet={packet} />
              {statusBadge(packet)}
            </Group>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
            <Field label="Report version" value={packet.reportVersion} />
            <Field label="Signed" value={formatInstantDate(packet.signedAt)} />
            <Field
              label="Recorded in Tebra"
              value={formatInstantDate(packet.submittedAt)}
            />
            <Field
              label="Date of service"
              value={formatManualTebraCalendarDate(packet.dateOfService)}
            />
            <Field
              label="Billing scenario"
              value={textOrDash(packet.billingScenario)}
            />
            <Field
              label="Direct to practice"
              value={
                packet.directToPractice === undefined
                  ? "—"
                  : packet.directToPractice
                  ? "Yes"
                  : "No"
              }
            />
            <Field label="Practice" value={textOrDash(packet.practice?.name)} />
            <Field
              label="Physician"
              value={textOrDash(packet.physician?.name)}
            />
            <Field
              label="Physician NPI"
              value={textOrDash(packet.physician?.npi)}
            />
            <Field
              label="Service location"
              value={textOrDash(
                packet.serviceLocation?.name || destination?.serviceLocationName
              )}
            />
            <Field
              label="Authorization"
              value={textOrDash(packet.authorizationNumber)}
            />
            <Field
              label="Tebra encounter"
              value={textOrDash(packet.tebraEncounterId)}
            />
            <Field
              label="Tebra patient"
              value={textOrDash(packet.tebraPatientId)}
            />
            <Field
              label="Tebra patient case"
              value={textOrDash(packet.tebraPatientCaseId)}
            />
            <Field label="Packet fingerprint" value={packet.packetHash} />
          </SimpleGrid>
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack gap="sm">
          <Title order={5}>Tebra destination</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
            <Field
              label="Practice ID"
              value={textOrDash(destination?.practiceId)}
            />
            <Field
              label="Rendering provider ID"
              value={textOrDash(destination?.renderingProviderId)}
            />
            <Field
              label="Rendering provider"
              value={textOrDash(destination?.renderingProviderName)}
            />
            <Field
              label="Service location ID"
              value={textOrDash(destination?.serviceLocationId)}
            />
            <Field
              label="Service location"
              value={textOrDash(destination?.serviceLocationName)}
            />
            <Field
              label="Payer scenario"
              value={textOrDash(destination?.payerScenario)}
            />
            <Field
              label="Primary claim type"
              value={textOrDash(destination?.primaryClaimType)}
            />
            <Field
              label="Encounter status"
              value={textOrDash(destination?.encounterStatus)}
            />
            <Field
              label="Patient statements"
              value={
                destination?.sendPatientStatements === null ||
                destination?.sendPatientStatements === undefined
                  ? "—"
                  : destination.sendPatientStatements
                  ? "Send"
                  : "Do not send"
              }
            />
            <Field
              label="Electronic secondary"
              value={
                destination?.disableElectronicSecondary === null ||
                destination?.disableElectronicSecondary === undefined
                  ? "—"
                  : destination.disableElectronicSecondary
                  ? "Disabled"
                  : "Enabled"
              }
            />
            <Field
              label="Business time zone"
              value={textOrDash(destination?.businessTimeZone)}
            />
          </SimpleGrid>
        </Stack>
      </Paper>

      <Paper withBorder p="md">
        <Stack gap="sm">
          <Title order={5}>Patient</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
            <Field label="Name" value={patientName(packet)} />
            <Field label="MRN" value={textOrDash(patient?.mrn)} />
            <Field
              label="DOB"
              value={formatManualTebraCalendarDate(patient?.dob)}
            />
            <Field label="Sex" value={textOrDash(patient?.sex)} />
            <Field
              label="Phone"
              value={textOrDash(
                patient?.mobilePhone || patient?.homePhone || patient?.phone
              )}
            />
            <Field label="Work phone" value={textOrDash(patient?.workPhone)} />
            <Field label="Email" value={textOrDash(patient?.email)} />
            <Field label="Address" value={formatAddress(patient?.address)} />
          </SimpleGrid>
        </Stack>
      </Paper>

      <InsuranceSection
        title="Primary insurance"
        insurance={packet.primaryInsurance}
      />
      <InsuranceSection
        title="Secondary insurance"
        insurance={packet.secondaryInsurance}
      />

      <Paper
        withBorder
        p="md"
        className={isLegacy ? "manual-tebra-legacy-lines" : undefined}
      >
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap="xs">
              <Title order={5}>
                {isLegacy
                  ? "Unverified legacy candidate service lines"
                  : "Signed service lines"}
              </Title>
              {isLegacy && (
                <Badge color="orange">UNVERIFIED · LEGACY LIVE</Badge>
              )}
            </Group>
            {!isLegacy && (
              <Text size="sm" fw={600}>
                Total charges: {formatMoney(totalManualTebraCharges(packet))}
              </Text>
            )}
          </Group>
          {lines.length === 0 ? (
            <Alert color="red">
              {isLegacy
                ? "Claim facts are intentionally suppressed because this case has no immutable signed billing snapshot. Sign a corrected/addendum report before Tebra entry."
                : packet.readiness.ready
                ? "No signed canonical service lines are available."
                : "Claim facts are intentionally suppressed until every readiness error is resolved and the packet is refreshed."}
            </Alert>
          ) : (
            <Table.ScrollContainer minWidth={900}>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>CPT</Table.Th>
                    <Table.Th>Modifiers</Table.Th>
                    <Table.Th>Units</Table.Th>
                    <Table.Th>Unit charge</Table.Th>
                    <Table.Th>Total</Table.Th>
                    <Table.Th>ICD-10 links</Table.Th>
                    <Table.Th>Specimens</Table.Th>
                    <Table.Th>Description / source</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {lines.map((line, index) => (
                    <Table.Tr
                      key={`${line.baseCode}-${line.modifiers.join(
                        "-"
                      )}-${index}`}
                    >
                      <Table.Td fw={700}>
                        <Group gap={4} wrap="nowrap">
                          <span>{line.baseCode}</span>
                          {isLegacy && (
                            <Badge size="xs" color="orange" variant="light">
                              Unverified
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>{line.modifiers.join(", ") || "—"}</Table.Td>
                      <Table.Td>{line.units}</Table.Td>
                      <Table.Td>{formatMoney(line.unitCharge)}</Table.Td>
                      <Table.Td>{formatMoney(line.totalCharge)}</Table.Td>
                      <Table.Td>
                        {line.diagnosisCodes.join(", ") || "—"}
                      </Table.Td>
                      <Table.Td>
                        {line.specimenLabels?.join(", ") || "—"}
                      </Table.Td>
                      <Table.Td>
                        {[line.description, line.source]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

function ManualEntryPacketModal({
  packet,
  onClose,
}: {
  packet: ManualTebraEntryCase;
  onClose: () => void;
}) {
  const canManage = useHasPermission("billing.manage");
  const {
    data: billingConfiguration,
    isError: isConfigurationError,
    isFetching: isConfigurationFetching,
    isLoading: isConfigurationLoading,
  } = useBillingConfiguration();
  const markMutation = useMarkManualEntry();
  const [encounterId, setEncounterId] = useState("");
  const [patientId, setPatientId] = useState("");
  const [patientCaseId, setPatientCaseId] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isLegacy = packet.billingFactsSource === "legacy_live";
  const alreadyEntered =
    packet.billingStatus === "submitted" || packet.billingStatus === "billed";
  const manualCompletionAllowed =
    !isConfigurationError &&
    !isConfigurationFetching &&
    !isConfigurationLoading &&
    billingConfiguration?.automationEnabled === false &&
    billingConfiguration.mode === "manual";
  const packetCanBeConfirmed = canConfirmManualTebraEntry(packet);

  const handleClose = () => {
    document.body.classList.remove("manual-tebra-printing");
    onClose();
  };

  const handlePrint = () => {
    const cleanup = () =>
      document.body.classList.remove("manual-tebra-printing");
    document.body.classList.add("manual-tebra-printing");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
  };

  const handleMarkEntered = () => {
    if (!packetCanBeConfirmed) return;
    const nextErrors = validateManualEntryIdentifiers({
      tebraEncounterId: encounterId,
      tebraPatientId: patientId,
      tebraPatientCaseId: patientCaseId,
    });
    setErrors(nextErrors as Record<string, string>);
    if (Object.keys(nextErrors).length > 0) return;

    markMutation.mutate(
      {
        caseId: packet.caseId,
        data: {
          tebraEncounterId: encounterId.trim(),
          tebraPatientId: patientId.trim() || undefined,
          tebraPatientCaseId: patientCaseId.trim() || undefined,
          note: note.trim() || undefined,
          expectedReportVersion: packet.reportVersion,
          packetHash: packet.packetHash,
        },
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <Modal
      opened
      onClose={handleClose}
      title={`Manual Tebra entry worksheet — ${packet.caseNumber}`}
      size="95%"
      classNames={{
        content: "manual-tebra-packet",
        close: "manual-tebra-no-print",
      }}
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Group justify="space-between" className="manual-tebra-no-print">
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              onClick={handlePrint}
              disabled={!packetCanBeConfirmed}
            >
              {packetCanBeConfirmed ? "Print worksheet" : "Printing blocked"}
            </Button>
            <Button
              component={Link}
              to={`/cases/${packet.caseId}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="subtle"
            >
              Open case
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Packet version {packet.reportVersion} · Integrity{" "}
            {packet.packetHash.slice(0, 12)}…
          </Text>
        </Group>

        <PacketDetails packet={packet} />

        {canManage &&
          !alreadyEntered &&
          manualCompletionAllowed &&
          isLegacy && (
            <Alert
              color="red"
              icon={<IconLock size={16} />}
              className="manual-tebra-no-print"
            >
              This legacy packet cannot be recorded as entered. Create and sign
              a corrected or addendum report so the LIS captures immutable
              billing lines, then refresh this queue.
            </Alert>
          )}
        {canManage &&
          !alreadyEntered &&
          manualCompletionAllowed &&
          !packetCanBeConfirmed &&
          !isLegacy && (
            <Alert
              color="red"
              icon={<IconLock size={16} />}
              className="manual-tebra-no-print"
            >
              This packet cannot be entered or recorded. Resolve every readiness
              error, then refresh the queue.
            </Alert>
          )}
        {canManage &&
          !alreadyEntered &&
          manualCompletionAllowed &&
          packetCanBeConfirmed &&
          !isLegacy && (
            <Paper withBorder p="md" className="manual-tebra-no-print">
              <Stack gap="md">
                <div>
                  <Title order={5}>Record completed manual entry</Title>
                  <Text size="sm" c="dimmed">
                    Only record this after the Draft encounter exists in Tebra.
                    This prevents a later automated submission from creating a
                    duplicate.
                  </Text>
                </div>
                <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                  Refresh the queue immediately before creating the Tebra Draft,
                  and record its Encounter ID here as soon as it is created. If
                  the LIS rejects a stale packet, stop and reconcile that
                  external Draft before doing anything else.
                </Alert>
                <Grid>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      required
                      label="Tebra encounter ID"
                      placeholder="Numeric ID"
                      value={encounterId}
                      error={errors.tebraEncounterId}
                      onChange={(event) =>
                        setEncounterId(event.currentTarget.value)
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="Tebra patient ID"
                      placeholder="Optional numeric ID"
                      value={patientId}
                      error={errors.tebraPatientId}
                      onChange={(event) =>
                        setPatientId(event.currentTarget.value)
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <TextInput
                      label="Tebra patient case ID"
                      placeholder="Optional numeric ID"
                      value={patientCaseId}
                      error={errors.tebraPatientCaseId}
                      onChange={(event) =>
                        setPatientCaseId(event.currentTarget.value)
                      }
                    />
                  </Grid.Col>
                </Grid>
                <Textarea
                  label="Internal note"
                  description="Optional audit note; do not copy credentials here."
                  value={note}
                  onChange={(event) => setNote(event.currentTarget.value)}
                  maxLength={1000}
                />
                <Group justify="flex-end">
                  <Button variant="default" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    color="green"
                    onClick={handleMarkEntered}
                    loading={markMutation.isPending}
                    disabled={!packetCanBeConfirmed}
                  >
                    Confirm manually entered
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}
        {canManage && !alreadyEntered && !manualCompletionAllowed && (
          <Alert
            color="red"
            icon={<IconLock size={16} />}
            className="manual-tebra-no-print"
          >
            Recording manual completion is unavailable unless the backend
            explicitly reports Manual mode. Refresh after the billing
            configuration is corrected.
          </Alert>
        )}
      </Stack>
    </Modal>
  );
}

export function ManualTebraEntryReportContent() {
  const labToday = useLabTodayDateOnly();
  const [urlSearchParams] = useSearchParams();
  const {
    data: billingConfiguration,
    isError: isConfigurationError,
    isFetching: isConfigurationFetching,
    isLoading: isConfigurationLoading,
  } = useBillingConfiguration();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ManualTebraStatusSelection>("attention");
  const [readiness, setReadiness] = useState<ManualTebraReadiness>("all");
  const [search, setSearch] = useState(
    () => urlSearchParams.get("search") ?? ""
  );
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [startDate, setStartDate] = useState<DateOnlyString | null>(null);
  const [endDate, setEndDate] = useState<DateOnlyString | null>(null);
  const [selectedPacket, setSelectedPacket] =
    useState<ManualTebraEntryCase | null>(null);

  const params = useMemo<ManualTebraEntryParams>(
    () => ({
      status: status === "attention" ? undefined : status,
      readiness,
      search: debouncedSearch.trim() || undefined,
      ...buildManualTebraDateParams(startDate, endDate),
    }),
    [status, readiness, debouncedSearch, startDate, endDate]
  );

  const { data, isLoading, isFetching, isError, error, refetch } =
    useManualTebraEntryReport(params, page, PAGE_SIZE);
  const awaitingCount = Math.max(
    0,
    (data?.summary.total ?? 0) - (data?.summary.alreadyEntered ?? 0)
  );
  const manualModeVerified =
    !isConfigurationError &&
    !isConfigurationFetching &&
    !isConfigurationLoading &&
    billingConfiguration?.mode === "manual";

  const handleStartDateChange = useCallback((value: unknown) => {
    setStartDate(normalizeManualTebraPickerDate(value));
    setPage(1);
  }, []);

  const handleEndDateChange = useCallback((value: unknown) => {
    setEndDate(normalizeManualTebraPickerDate(value));
    setPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleExport = useCallback(
    () => reportsService.exportManualTebraEntry(params),
    [params]
  );

  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(1, data.pagination.totalPages || 1);
    if (page > lastPage) setPage(lastPage);
  }, [data, page]);

  return (
    <Stack gap="lg">
      <Alert
        color={manualModeVerified ? "blue" : "red"}
        icon={<IconFileInvoice size={18} />}
      >
        <Text size="sm" fw={600}>
          {manualModeVerified
            ? "Manual billing mode"
            : "Billing worksheet — view/export only"}
        </Text>
        <Text size="sm">
          {manualModeVerified
            ? "Use only READY signed-snapshot packets to create the patient, insurance case, and Draft encounter in Tebra. Legacy packets are BLOCKED and must not be entered until a corrected/addendum report is signed."
            : billingConfiguration?.message ||
              "Billing configuration is loading or unavailable. Remote and manual completion actions are safely disabled."}
        </Text>
      </Alert>

      <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
        {[
          ["Awaiting entry", awaitingCount, "blue"],
          ["Signed ready", data?.summary.ready ?? 0, "green"],
          ["Legacy blocked", data?.summary.legacyBlocked ?? 0, "orange"],
          ["All blocked", data?.summary.blocked ?? 0, "red"],
          ["Already entered", data?.summary.alreadyEntered ?? 0, "grape"],
        ].map(([label, value, color]) => (
          <Paper
            key={String(label)}
            p="sm"
            withBorder
            style={{ borderLeft: `4px solid var(--mantine-color-${color}-5)` }}
          >
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              {label}
            </Text>
            <Text size="xl" fw={700}>
              {value}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      <Paper withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-end" wrap="wrap">
            <Group align="flex-end" wrap="wrap">
              <TextInput
                label="Search"
                placeholder="Case #, patient, MRN…"
                leftSection={<IconSearch size={15} />}
                value={search}
                onChange={(event) => {
                  setSearch(event.currentTarget.value);
                  setPage(1);
                }}
                w={240}
              />
              <Select
                label="Status"
                data={STATUS_OPTIONS}
                value={status}
                allowDeselect={false}
                onChange={(value) => {
                  setStatus(
                    (value as ManualTebraStatusSelection) || "attention"
                  );
                  setPage(1);
                }}
                w={165}
              />
              <Select
                label="Readiness"
                data={READINESS_OPTIONS}
                value={readiness}
                allowDeselect={false}
                onChange={(value) => {
                  setReadiness((value as ManualTebraReadiness) || "all");
                  setPage(1);
                }}
                w={150}
              />
              <DatePickerInput
                label="Signed from"
                placeholder="All dates"
                value={startDate}
                onChange={handleStartDateChange}
                clearable
                maxDate={endDate ?? labToday}
                w={155}
              />
              <DatePickerInput
                label="Signed through"
                placeholder="All dates"
                value={endDate}
                onChange={handleEndDateChange}
                clearable
                minDate={startDate ?? undefined}
                maxDate={labToday}
                w={155}
              />
              <Tooltip label="Refresh packets">
                <ActionIcon
                  variant="default"
                  size="lg"
                  onClick={handleRefresh}
                  loading={isFetching}
                  aria-label="Refresh packets"
                >
                  <IconRefresh size={17} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Stack gap={3} align="flex-end">
              <ReportExportButton
                filename={`manual-tebra-entry-${labToday}.csv`}
                fetchData={handleExport}
              />
              <Text size="xs" c="orange.9" maw={390} ta="right">
                <IconLock size={11} style={{ verticalAlign: "-1px" }} /> This
                CSV contains PHI and insurance data. Store and share it only
                through approved secure channels. Legacy rows are labeled
                BLOCKED; their service-line fields are intentionally blank and
                must not be entered into Tebra.
              </Text>
            </Stack>
          </Group>
          {!startDate && !endDate && (
            <Text size="xs" c="dimmed">
              No date limit is applied, so older cases still awaiting manual
              entry remain visible.
            </Text>
          )}
        </Stack>
      </Paper>

      <Paper withBorder>
        {isError ? (
          <Alert color="red" m="md" icon={<IconAlertTriangle size={18} />}>
            The manual Tebra report could not be loaded. No cases are shown
            until the report loads successfully. {error?.message}
          </Alert>
        ) : isLoading ? (
          <Stack p="md" gap="xs">
            {[...Array(6)].map((_, index) => (
              <Skeleton key={index} h={40} />
            ))}
          </Stack>
        ) : !data?.data.length ? (
          <Center py="xl">
            <Text c="dimmed">
              No signed billing packets match these filters.
            </Text>
          </Center>
        ) : (
          <>
            <Table.ScrollContainer minWidth={1080}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Readiness</Table.Th>
                    <Table.Th>Case #</Table.Th>
                    <Table.Th>Patient / MRN</Table.Th>
                    <Table.Th>Primary payer</Table.Th>
                    <Table.Th>Signed</Table.Th>
                    <Table.Th>Service lines</Table.Th>
                    <Table.Th>Total charges</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Worksheet</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.data.map((packet) => (
                    <Table.Tr key={`${packet.caseId}-${packet.reportVersion}`}>
                      <Table.Td>
                        <ReadinessBadge packet={packet} />
                      </Table.Td>
                      <Table.Td>
                        <Text fw={700} size="sm">
                          {packet.caseNumber}
                        </Text>
                        <Text size="xs" c="dimmed">
                          v{packet.reportVersion}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{patientName(packet)}</Text>
                        <Text size="xs" c="dimmed">
                          MRN {textOrDash(packet.patient?.mrn)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {textOrDash(packet.primaryInsurance?.providerName)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {textOrDash(packet.primaryInsurance?.planTypeName)}
                        </Text>
                      </Table.Td>
                      <Table.Td>{formatInstantDate(packet.signedAt)}</Table.Td>
                      <Table.Td>
                        {!packet.readiness.ready
                          ? "Blocked"
                          : getManualTebraServiceLines(packet).length}
                      </Table.Td>
                      <Table.Td>
                        {!packet.readiness.ready
                          ? "Suppressed"
                          : formatMoney(totalManualTebraCharges(packet))}
                      </Table.Td>
                      <Table.Td>
                        {statusBadge(packet)}
                        {packet.submittedAt && (
                          <Text size="xs" c="dimmed" mt={3}>
                            {formatInstantDate(packet.submittedAt)}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="compact-sm"
                          variant="light"
                          leftSection={<IconEye size={14} />}
                          onClick={() => setSelectedPacket(packet)}
                        >
                          View packet
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {(data.pagination.totalPages ?? 1) > 1 && (
              <Group justify="space-between" p="md">
                <Text size="sm" c="dimmed">
                  {data.pagination.total} packets
                </Text>
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={data.pagination.totalPages}
                />
              </Group>
            )}
          </>
        )}
      </Paper>

      {selectedPacket && (
        <ManualEntryPacketModal
          packet={selectedPacket}
          onClose={() => setSelectedPacket(null)}
        />
      )}
    </Stack>
  );
}

export default function ManualTebraEntryReportPage() {
  const canReadBilling = useHasPermission("billing.read");
  if (!canReadBilling) {
    return (
      <Alert color="red" m="md" icon={<IconLock size={18} />}>
        Billing read permission is required to access this PHI report.
      </Alert>
    );
  }
  return (
    <Stack gap="lg" p="md">
      <Group className="manual-tebra-no-print">
        <Button
          component={Link}
          to="/reports"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
        >
          Reports
        </Button>
      </Group>
      <ManualTebraEntryReportContent />
    </Stack>
  );
}
